import { useCallback, useEffect, useState } from 'react';
import useTranslation from 'v2/layouts/OrganizationWorkspaceLayout/components/ControllerTableSection/hooks/useTranslation';
import type { Controller } from 'v2/layouts/OrganizationWorkspaceLayout/components/ControllerTableSection/types';

import useControllers from 'hooks/useControllers';
import useToast from 'hooks/useToast';

const useControllerTable = () => {
    const { deleteController, fetchControllers } = useControllers();
    const { broadcastToast } = useToast();
    const { DELETE_ERROR, DELETE_SUCCESS, LOAD_ERROR } = useTranslation();

    const [controllers, setControllers] = useState<Controller[]>([]);
    const [nextPageToken, setNextPageToken] = useState<string | null>(null);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);

    const loadPage = useCallback(
        async (pageToken?: string | null) => {
            const response = await fetchControllers(pageToken);

            setControllers(currentControllers =>
                pageToken ? [...currentControllers, ...response.objects] : response.objects
            );
            setHasNextPage(response.hasNext);
            setNextPageToken(response.nextPageToken ?? null);
        },
        [fetchControllers]
    );

    useEffect(() => {
        const loadFirstPage = async () => {
            try {
                await loadPage();
            } catch {
                broadcastToast({ text: LOAD_ERROR, type: 'error' });
            } finally {
                setIsLoading(false);
            }
        };

        void loadFirstPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLoadMore = useCallback(async () => {
        if (!hasNextPage || isFetchingMore) {
            return;
        }

        setIsFetchingMore(true);

        try {
            await loadPage(nextPageToken);
        } catch {
            broadcastToast({ text: LOAD_ERROR, type: 'error' });
        } finally {
            setIsFetchingMore(false);
        }
    }, [LOAD_ERROR, broadcastToast, hasNextPage, isFetchingMore, loadPage, nextPageToken]);

    const handleDelete = useCallback(
        async (controllerId: string) => {
            try {
                await deleteController(controllerId);
            } catch {
                broadcastToast({ text: DELETE_ERROR, type: 'error' });

                return;
            }

            setControllers(currentControllers =>
                currentControllers.filter(controllerItem => controllerItem.id !== controllerId)
            );

            broadcastToast({ text: DELETE_SUCCESS, type: 'success' });
        },
        [DELETE_ERROR, DELETE_SUCCESS, broadcastToast, deleteController]
    );

    return {
        controllers,
        handleDelete,
        handleLoadMore,
        hasNextPage,
        isFetchingMore,
        isLoading
    };
};

export default useControllerTable;
